import { LeadCoordinatorReviewDtoIn, LeadCoordinatorToReviewDtoOut } from '@/models/out/LeadCoordinatorToReviewDtoOut';

export type FundRef = { fundId: number; fundName: string | null };

/**
 * One coordinator with every project where they wait. The server answers per project, but one
 * decision settles them all - the account and the record holding its number are the same in each -
 * so the office sees each person once (2.11.2026, after the first appointment run: 63 rows were 49
 * people, one of them in five projects).
 */
export type CoordinatorToReview = LeadCoordinatorToReviewDtoOut & { funds: FundRef[] };

const fullName = (first: string | null, last: string | null) => `${first ?? ''} ${last ?? ''}`.replace(/\s+/g, ' ').trim();
export const nameOf = (row: LeadCoordinatorToReviewDtoOut) => fullName(row.firstName, row.lastName) || row.email;
export const recordNameOf = (row: LeadCoordinatorToReviewDtoOut) => fullName(row.recordFirstName, row.recordLastName);
export const fundLabel = (fund: FundRef) => (fund.fundName ? `${fund.fundName.trim()} (${fund.fundId})` : `${fund.fundId}`);
/** One project by its name, several by their numbers - what a sentence about the coordinator can hold. */
export const fundsText = (row: CoordinatorToReview) => (row.funds.length === 1 ? fundLabel(row.funds[0]) : row.funds.map((f) => f.fundId).join(', '));

export const byCoordinator = (rows: LeadCoordinatorToReviewDtoOut[]): CoordinatorToReview[] => {
  const people = new Map<string, CoordinatorToReview>();
  for (const row of rows) {
    const key = row.email.trim().toLowerCase();
    const fund = { fundId: row.fundId, fundName: row.fundName };
    const person = people.get(key);
    if (person) person.funds.push(fund);
    else people.set(key, { ...row, funds: [fund] });
  }
  return [...people.values()].sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'he'));
};

/** Every action names one project; the server settles the coordinator in every project where they wait. */
export const requestFor = (row: CoordinatorToReview): LeadCoordinatorReviewDtoIn => ({ fundId: row.funds[0].fundId, email: row.email });
